import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Paper,
  Divider,
  InputAdornment,
  Tooltip,
  Fab,
  Container,
  useTheme
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Category,
  DataObject,
  Assessment,
  Save,
  Cancel,
  Refresh,
  FilterList,
  CloudUpload,
  GetApp,
  CheckCircle,
  Error as ErrorIcon
} from '@mui/icons-material';
import axios from 'axios';

interface CategoryItem {
  id: number;
  name: string;
  code: string;
  category_id: number;
}

interface Category {
  id: number;
  name: string;
  code: string;
  items: CategoryItem[];
}

const Input: React.FC = () => {
  const theme = useTheme();
  const [categories, setCategories] = useState<Category[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);

  // Dialog states
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Form states
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryCode, setNewCategoryCode] = useState('');
  const [newItemName, setNewItemName] = useState('');
  const [newItemCode, setNewItemCode] = useState('');

  // Edit states
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [editingItem, setEditingItem] = useState<CategoryItem | null>(null);

  // Delete states
  const [deleteTarget, setDeleteTarget] = useState<{
    type: 'category' | 'item';
    categoryId: number;
    itemId?: number;
  } | null>(null);

  // Excel import states
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    message: string;
    summary?: {
      categories_imported: number;
      items_imported: number;
      errors_count: number;
    };
    errors?: string[];
  } | null>(null);

  // Template generation states
  const [generatingTemplate, setGeneratingTemplate] = useState(false);
  const [templateGenerated, setTemplateGenerated] = useState(false);
  const [templateFileName, setTemplateFileName] = useState<string>('');

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/v1/input/categories', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch categories');
      setLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/v1/input/categories', {
        name: newCategoryName,
        code: newCategoryCode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewCategoryName('');
      setNewCategoryCode('');
      setCategoryDialogOpen(false);
      fetchCategories();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create category');
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/v1/input/categories/${editingCategory.id}`, {
        name: editingCategory.name,
        code: editingCategory.code
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingCategory(null);
      fetchCategories();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update category');
    }
  };

  const handleDeleteCategory = (categoryId: number) => {
    setDeleteTarget({ type: 'category', categoryId });
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteCategory = async () => {
    if (!deleteTarget || deleteTarget.type !== 'category') return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/v1/input/categories/${deleteTarget.categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
      fetchCategories();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete category');
    }
  };

  const handleCreateItem = async () => {
    if (!selectedCategory) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/v1/input/categories/${selectedCategory.id}/items`, {
        name: newItemName,
        code: newItemCode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewItemName('');
      setNewItemCode('');
      setItemDialogOpen(false);
      setSelectedCategory(null);
      fetchCategories();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create item');
    }
  };

  const handleUpdateItem = async () => {
    if (!editingItem) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/v1/input/categories/${editingItem.category_id}/items/${editingItem.id}`, {
        name: editingItem.name,
        code: editingItem.code
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingItem(null);
      fetchCategories();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update item');
    }
  };

  const handleDeleteItem = (categoryId: number, itemId: number) => {
    setDeleteTarget({ type: 'item', categoryId, itemId });
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteItem = async () => {
    if (!deleteTarget || deleteTarget.type !== 'item') return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/v1/input/categories/${deleteTarget.categoryId}/items/${deleteTarget.itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
      fetchCategories();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete item');
    }
  };

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setDeleteTarget(null);
  };

  // Template generation and download functions
  const handleGenerateTemplate = async () => {
    setGeneratingTemplate(true);
    setTemplateGenerated(false);
    setTemplateFileName('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/v1/input/generate-template', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.file_name) {
        setTemplateGenerated(true);
        setTemplateFileName(response.data.file_name);
        setError(null);
      } else {
        setError('Template generation failed - no file name returned');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate template');
      setTemplateGenerated(false);
    } finally {
      setGeneratingTemplate(false);
    }
  };

  const handleDownloadTemplate = async () => {
    if (!templateGenerated || !templateFileName) {
      setError('Please generate the template first');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/v1/input/download-template/${templateFileName}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Descriptive_Data_Codes_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Reset template state after successful download
      setTemplateGenerated(false);
      setTemplateFileName('');
    } catch (err: any) {
      setError('Failed to download template');
    }
  };

  const handleExcelImport = async (file: File) => {
    setImporting(true);
    setImportResult(null);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post('/api/v1/input/import-excel', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResult(response.data);
      fetchCategories(); // Refresh the data
    } catch (err: any) {
      setImportResult({
        message: err.response?.data?.message || 'Import failed',
        summary: { categories_imported: 0, items_imported: 0, errors_count: 1 },
        errors: [err.response?.data?.message || 'Unknown error occurred']
      });
    } finally {
      setImporting(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleExcelImport(file);
    }
    // Reset the input
    event.target.value = '';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Calculate statistics
  const totalCategories = categories.length;
  const totalItems = categories.reduce((sum, cat) => sum + cat.items.length, 0);
  const averageItemsPerCategory = totalCategories > 0 ? Math.round(totalItems / totalCategories) : 0;

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
              Descriptive Data Codes
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage categories and items for credit risk assessment
            </Typography>
          </Box>
          <Tooltip title="Add new category">
            <Fab
              color="primary"
              size="medium"
              onClick={() => setCategoryDialogOpen(true)}
              sx={{
                boxShadow: theme.shadows[4],
                '&:hover': {
                  boxShadow: theme.shadows[8],
                }
              }}
            >
              <Add />
            </Fab>
          </Tooltip>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              '&:hover': { transform: 'translateY(-2px)', transition: 'transform 0.2s' }
            }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {totalCategories}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Categories
                    </Typography>
                  </Box>
                  <Category sx={{ fontSize: 32, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              '&:hover': { transform: 'translateY(-2px)', transition: 'transform 0.2s' }
            }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {totalItems}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Total Items
                    </Typography>
                  </Box>
                  <DataObject sx={{ fontSize: 32, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card sx={{
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
              '&:hover': { transform: 'translateY(-2px)', transition: 'transform 0.2s' }
            }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {averageItemsPerCategory}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Avg. Items/Category
                    </Typography>
                  </Box>
                  <Assessment sx={{ fontSize: 32, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Excel Import/Export Section */}
        <Card sx={{ mb: 3, border: '2px dashed', borderColor: 'primary.main', bgcolor: 'primary.50' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Excel Import/Export
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Import multiple categories and items from Excel or download a template to get started.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<Save />}
                onClick={handleGenerateTemplate}
                disabled={generatingTemplate}
                sx={{ minWidth: 180 }}
              >
                {generatingTemplate ? <CircularProgress size={20} /> : 'Generate Template'}
              </Button>

              <Button
                variant="outlined"
                startIcon={<GetApp />}
                onClick={handleDownloadTemplate}
                disabled={!templateGenerated || generatingTemplate}
                sx={{ minWidth: 180 }}
              >
                {templateGenerated ? 'Download Template' : 'Download Template (Generate First)'}
              </Button>

              <Button
                variant="contained"
                startIcon={<CloudUpload />}
                onClick={() => setImportDialogOpen(true)}
                disabled={importing}
                sx={{ minWidth: 180 }}
              >
                {importing ? <CircularProgress size={20} /> : 'Import Excel'}
              </Button>

              <input
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                id="excel-file-input"
                type="file"
                onChange={handleFileUpload}
              />
              <label htmlFor="excel-file-input">
                <Button
                  variant="text"
                  component="span"
                  sx={{ color: 'text.secondary' }}
                >
                  Select File
                </Button>
              </label>
            </Box>

            {/* Template Generation Status */}
            {templateGenerated && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="success" sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Template generated successfully! Click "Download Template" to download the file.
                    </Typography>
                  </Box>
                </Alert>
              </Box>
            )}

            {/* Import Results */}
            {importResult && (
              <Box sx={{ mt: 3 }}>
                <Alert
                  severity={importResult.summary?.errors_count === 0 ? 'success' : 'warning'}
                  sx={{ mb: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {importResult.summary?.errors_count === 0 ? (
                      <CheckCircle sx={{ fontSize: 20 }} />
                    ) : (
                      <ErrorIcon sx={{ fontSize: 20 }} />
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {importResult.message}
                    </Typography>
                  </Box>
                </Alert>

                {importResult.summary && (
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Chip
                      label={`${importResult.summary.categories_imported} categories imported`}
                      color="primary"
                      size="small"
                    />
                    <Chip
                      label={`${importResult.summary.items_imported} items imported`}
                      color="secondary"
                      size="small"
                    />
                    {importResult.summary.errors_count > 0 && (
                      <Chip
                        label={`${importResult.summary.errors_count} errors`}
                        color="error"
                        size="small"
                      />
                    )}
                  </Box>
                )}

                {importResult.errors && importResult.errors.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
                      Errors encountered:
                    </Typography>
                    <Box sx={{ maxHeight: 150, overflow: 'auto', bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                      {importResult.errors.map((error, index) => (
                        <Typography key={index} variant="body2" color="error" sx={{ mb: 0.5 }}>
                          • {error}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Search and Actions Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <TextField
            placeholder="Search categories..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh data">
              <IconButton onClick={fetchCategories} sx={{ color: 'primary.main' }}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Filter options">
              <IconButton sx={{ color: 'primary.main' }}>
                <FilterList />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      {/* Categories Grid */}
      {categories.length === 0 ? (
        <Card sx={{ p: 6, textAlign: 'center', backgroundColor: 'grey.50' }}>
          <Category sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No categories found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Get started by adding your first descriptive data category
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCategoryDialogOpen(true)}
            size="large"
          >
            Add First Category
          </Button>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {categories
            .filter(category =>
              category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              category.items.some(item =>
                item.name.toLowerCase().includes(searchQuery.toLowerCase())
              )
            )
            .map((category) => (
              <Grid item xs={12} lg={6} key={category.id}>
                <Card sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    boxShadow: theme.shadows[4],
                    borderColor: 'primary.main'
                  }
                }}>
                  {/* Category Header */}
                  <Box sx={{
                    p: 3,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.primary.main}04 100%)`
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          {category.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Chip
                            label={`Code: ${category.code}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          <Typography variant="body2" color="text.secondary">
                            {category.items.length} items
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Add item">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedCategory(category);
                              setItemDialogOpen(true);
                            }}
                            sx={{ color: 'success.main' }}
                          >
                            <Add />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit category">
                          <IconButton
                            size="small"
                            onClick={() => setEditingCategory(category)}
                            sx={{ color: 'primary.main' }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete category">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteCategory(category.id)}
                            sx={{ color: 'error.main' }}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>

                  {/* Items Table */}
                  <Box sx={{ flex: 1, p: 0 }}>
                    {category.items.length === 0 ? (
                      <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                        <DataObject sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                        <Typography variant="body2">No items in this category</Typography>
                        <Button
                          size="small"
                          startIcon={<Add />}
                          onClick={() => {
                            setSelectedCategory(category);
                            setItemDialogOpen(true);
                          }}
                          sx={{ mt: 1 }}
                        >
                          Add Item
                        </Button>
                      </Box>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: 'grey.50' }}>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Name</TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Code</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.875rem', width: 80 }}>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {category.items.map((item) => (
                              <TableRow key={item.id} hover>
                                <TableCell sx={{ fontSize: '0.875rem' }}>{item.name}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={item.code}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.75rem', height: 24 }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Tooltip title="Edit item">
                                    <IconButton
                                      size="small"
                                      onClick={() => setEditingItem(item)}
                                      sx={{ color: 'primary.main', mr: 0.5 }}
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete item">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteItem(category.id, item.id)}
                                      sx={{ color: 'error.main' }}
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                </Card>
              </Grid>
            ))}
        </Grid>
      )}

        {/* Category Dialog */}
        <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add New Category</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Category Name"
              fullWidth
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Category Code"
              fullWidth
              value={newCategoryCode}
              onChange={(e) => setNewCategoryCode(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateCategory} variant="contained">Create</Button>
          </DialogActions>
        </Dialog>

        {/* Item Dialog */}
        <Dialog open={itemDialogOpen} onClose={() => setItemDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add New Item</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Item Name"
              fullWidth
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Item Code"
              fullWidth
              value={newItemCode}
              onChange={(e) => setNewItemCode(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setItemDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateItem} variant="contained">Create</Button>
          </DialogActions>
        </Dialog>

        {/* Edit Category Dialog */}
        <Dialog open={!!editingCategory} onClose={() => setEditingCategory(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Category</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Category Name"
              fullWidth
              value={editingCategory?.name || ''}
              onChange={(e) => setEditingCategory(prev => prev ? {...prev, name: e.target.value} : null)}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Category Code"
              fullWidth
              value={editingCategory?.code || ''}
              onChange={(e) => setEditingCategory(prev => prev ? {...prev, code: e.target.value} : null)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditingCategory(null)}>Cancel</Button>
            <Button onClick={handleUpdateCategory} variant="contained">Update</Button>
          </DialogActions>
        </Dialog>

        {/* Edit Item Dialog */}
        <Dialog open={!!editingItem} onClose={() => setEditingItem(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Item</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Item Name"
              fullWidth
              value={editingItem?.name || ''}
              onChange={(e) => setEditingItem(prev => prev ? {...prev, name: e.target.value} : null)}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Item Code"
              fullWidth
              value={editingItem?.code || ''}
              onChange={(e) => setEditingItem(prev => prev ? {...prev, code: e.target.value} : null)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditingItem(null)}>Cancel</Button>
            <Button onClick={handleUpdateItem} variant="contained">Update</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onClose={handleCloseDeleteConfirm} maxWidth="sm" fullWidth>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>
              {deleteTarget?.type === 'category'
                ? 'Are you sure you want to delete this category and all its items? This action cannot be undone.'
                : 'Are you sure you want to delete this item? This action cannot be undone.'
              }
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteConfirm}>Cancel</Button>
            <Button
              onClick={deleteTarget?.type === 'category' ? confirmDeleteCategory : confirmDeleteItem}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
    </Box>
  );
};

export default Input;
